import { useTheme } from 'foundations-theming/utils';
import { useEffect, useState } from 'react';
import { Route, Switch, generatePath, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { parseLocation } from 'automation-ui-routing/utils';
import { NavMarker } from 'react-rhumb';
import PropTypes from 'prop-types';
import UIButton from 'UIComponents/button/UIButton';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { isFilterEmpty } from 'automation-ui-flow-core/enrollment/enrollmentUtils';
import { getEventTrackingDataWithTriggers } from 'automation-ui-enrollment/utils/trackingUtils';
import EnrollmentSaveButton from 'automation-ui-enrollment/components/common/EnrollmentSaveButton';
import TriggerTypeChangeButton from 'automation-ui-enrollment/components/eventEnrollment/TriggerTypeChangeButton';
import EnrollmentActionConfigHeader from 'automation-ui-enrollment/components/common/EnrollmentActionConfigHeader';
import Routes from 'automation-ui-enrollment/constants/Routes';
import ListMembershipErrorBanner, { shouldShowListMembershipError } from 'automation-ui-enrollment/components/eventEnrollment/ListMembershipErrorBanner';
import PropertyValueChangedWarningBanner, { shouldShowPropertyValueChangedWarning } from 'automation-ui-enrollment/components/eventEnrollment/PropertyValueChangedWarningBanner';
import EventEnrollmentReview from 'automation-ui-enrollment/components/eventEnrollment/steps/EventEnrollmentReview';
import WebhookSelectionStep from 'automation-ui-enrollment/components/eventEnrollment/steps/WebhookSelectionStep';
import EventReenrollmentView from 'automation-ui-enrollment/components/eventEnrollment/EventReenrollmentView';
import UIAlert from 'UIComponents/alert/UIAlert';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import { atomicQueryUpdate } from 'automation-ui-routing/historyUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getDisabledSaveMessage = ({
  areFiltersIncomplete,
  showListMembershipError
}) => {
  if (areFiltersIncomplete) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-enrollment.redesign.saveButton.tooltip.editingFilters"
    });
  }
  if (showListMembershipError) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-enrollment.redesign.saveButton.tooltip.listMembershipError"
    });
  }
  return null;
};
const getErrorData = ({
  stagedChanges,
  setStagedChanges,
  areFiltersIncomplete
}) => {
  const showListMembershipError = shouldShowListMembershipError(stagedChanges.triggers);
  const showPropertyValueChangedWarning = shouldShowPropertyValueChangedWarning(stagedChanges.triggers);
  let bannerComponent;
  if (showListMembershipError) {
    bannerComponent = /*#__PURE__*/_jsx(ListMembershipErrorBanner, {
      stagedChanges: stagedChanges,
      setStagedChanges: setStagedChanges
    });
  } else if (showPropertyValueChangedWarning) {
    // shouldShowPropertyValueChangedWarning checks if triggers are defined
    bannerComponent = /*#__PURE__*/_jsx(PropertyValueChangedWarningBanner, {
      triggers: stagedChanges.triggers
    });
  }
  const saveDisabledMessage = getDisabledSaveMessage({
    areFiltersIncomplete,
    showListMembershipError
  });
  return {
    hasError: showListMembershipError,
    bannerComponent,
    saveDisabledMessage
  };
};
const EventEnrollmentSteps = {
  ENROLLMENT_REVIEW: EventEnrollmentReview,
  WEBHOOK_SELECTION: WebhookSelectionStep
};
const StepConfig = {
  ENROLLMENT_REVIEW: {
    showBackButton: false,
    showChangeTriggerButton: true
  },
  WEBHOOK_SELECTION: {
    showBackButton: true,
    showChangeTriggerButton: false,
    backStep: 'ENROLLMENT_REVIEW'
  }
};
const EventEnrollmentContainerFromSegmentationFilters = ({
  flow,
  onClose,
  updateEnrollmentCriteria,
  readOnly = false,
  settingsComponent,
  unenrollmentComponent,
  shouldReenroll,
  setShouldReenroll
}) => {
  const trellisTheme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const trackEvent = useTrackEvent();
  const enrollmentCriteria = flow.enrollmentCriteria;
  const {
    triggers: initialTriggers,
    refinementCriteria: initialRefinementCriteria
  } = !enrollmentCriteria || enrollmentCriteria.triggerType === 'LIST' ? {
    triggers: undefined,
    refinementCriteria: undefined
  } : enrollmentCriteria;
  const {
    params: {
      revisionId
    }
  } = useRouteMatch();
  const [stagedChanges, setStagedChanges] = useState({
    refinementCriteria: initialRefinementCriteria,
    triggers: initialTriggers
  });
  const [saveStatus, setSaveStatus] = useState('UNINITIALIZED');

  // Keep track of whether the user is editing filters so we can disable the save button
  const [areFiltersIncomplete, setAreFiltersIncomplete] = useState(false);

  // Run tracking hook on mount
  useEffect(() => {
    trackEvent(Trackables.OPENED_ENROLLMENT, {
      enrollmentType: 'EVENT'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSaveEnrollment = () => {
    const stagedEnrollmentCriteriaValue = Object.assign({
      triggers: stagedChanges.triggers
    }, stagedChanges.refinementCriteria && stagedChanges.refinementCriteria.filterBranches.length ? {
      refinementCriteria: stagedChanges.refinementCriteria
    } : {}, {
      triggerType: 'EVENT'
    });
    let stagedEnrollmentCriteria = {
      value: stagedEnrollmentCriteriaValue,
      operation: 'UPSERT'
    };
    if (!stagedChanges.triggers || !stagedChanges.triggers.filterBranches.length) {
      stagedEnrollmentCriteria = {
        operation: 'DELETE'
      };
    }
    const updatePayload = {
      enrollmentCriteria: stagedEnrollmentCriteria,
      enrollmentTrigger: 'EVENT'
    };
    const trackingData = getEventTrackingDataWithTriggers({
      triggers: stagedChanges.triggers,
      refinementCriteria: stagedChanges.refinementCriteria
    });
    trackEvent(Trackables.SAVED_ENROLLMENT, Object.assign({}, trackingData, {
      shouldReenroll,
      objectTypeId: flow.objectTypeId,
      enrollmentType: 'EVENT',
      editType: (flow === null || flow === void 0 ? void 0 : flow.enrollmentTrigger) !== 'EVENT' ? 'ADD' : 'EDIT'
    }));
    setSaveStatus('STARTED');
    updateEnrollmentCriteria(updatePayload, () => {
      setSaveStatus('SUCCEEDED');
      onClose();
    }, () => setSaveStatus('FAILED'));
  };
  const clearEventTriggersSelectableView = callback => {
    // wipe existing enrollment data
    updateEnrollmentCriteria({
      enrollmentCriteria: {
        operation: 'DELETE'
      },
      enrollmentTrigger: 'NEW'
    }, callback, err => {
      reportError(err);
    });
  };
  const {
    hasError,
    bannerComponent,
    saveDisabledMessage
  } = getErrorData({
    stagedChanges,
    setStagedChanges,
    areFiltersIncomplete
  });
  const initialPanelStepString = parseLocation(location).initialPanelStep;
  const initialPanelStep = ['ENROLLMENT_REVIEW', 'WEBHOOK_SELECTION'].includes(initialPanelStepString) ? initialPanelStepString : 'ENROLLMENT_REVIEW';
  const [panelStep, setPanelStep] = useState(initialPanelStep);
  const changePanelStep = newPanelStep => {
    atomicQueryUpdate({}, ['initialPanelStep'], history);
    setPanelStep(newPanelStep);
  };
  useEffect(() => {
    setAreFiltersIncomplete(panelStep === 'WEBHOOK_SELECTION');
  }, [panelStep, setAreFiltersIncomplete]);
  const StepComponent = EventEnrollmentSteps[panelStep];
  const isInitialStep = initialPanelStep === panelStep;
  const stepConfig = StepConfig[panelStep];
  const showBackButton = stepConfig.showBackButton && !isInitialStep;
  const showChangeTriggerButton = stepConfig.showChangeTriggerButton || isInitialStep;
  const changeTriggersElement = showChangeTriggerButton && /*#__PURE__*/_jsx(TriggerTypeChangeButton, {
    triggerType: "EVENT",
    flowId: flow.flowId,
    resetFilters: clearEventTriggersSelectableView,
    hasExistingFilters: !isFilterEmpty(initialTriggers) || !isFilterEmpty(stagedChanges.triggers),
    readOnly: readOnly
  });
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "EVENT_ENROLLMENT_PANEL",
    children: /*#__PURE__*/_jsxs(Route, {
      path: [Routes.FLOW_CANVAS_EVENT_ENROLLMENT, Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG_EVENT, Routes.PLATFORM_FLOW_REVIEW_ENROLLMENT],
      children: [/*#__PURE__*/_jsx(EnrollmentActionConfigHeader, {
        header: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-enrollment.enrollmentHeader"
        }),
        showBackButton: showBackButton && initialPanelStep !== panelStep,
        onBack: () => changePanelStep(stepConfig.backStep),
        enrollmentPath: revisionId && !isNaN(parseInt(revisionId, 10)) ? generatePath(Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG_EVENT, {
          screen: 'edit',
          flowId: flow.flowId,
          revisionId
        }) : generatePath(Routes.FLOW_CANVAS_EVENT_ENROLLMENT, {
          screen: 'edit',
          flowId: flow.flowId
        }),
        unenrollmentPath: revisionId && !isNaN(parseInt(revisionId, 10)) ? generatePath(Routes.FLOW_CANVAS_REVISION_UNENROLLMENT_CONFIG_EVENT, {
          screen: 'edit',
          flowId: flow.flowId,
          revisionId
        }) : generatePath(Routes.FLOW_CANVAS_EVENT_UNENROLLMENT, {
          screen: 'edit',
          flowId: flow.flowId
        }),
        reenrollmentPath: revisionId && !isNaN(parseInt(revisionId, 10)) ? generatePath(Routes.FLOW_CANVAS_REVISION_REENROLLMENT_CONFIG_EVENT, {
          screen: 'edit',
          flowId: flow.flowId,
          revisionId
        }) : generatePath(Routes.FLOW_CANVAS_EVENT_REENROLLMENT, {
          screen: 'edit',
          flowId: flow.flowId
        }),
        children: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(UIButton, {
            onClick: onClose,
            responsive: false,
            use: "tertiary-light",
            size: "small",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-enrollment.redesign.cancelButton"
            })
          }), /*#__PURE__*/_jsx(EnrollmentSaveButton, {
            use: "tertiary",
            size: "small",
            saveFailed: saveStatus === 'FAILED',
            savePending: saveStatus === 'STARTED',
            onSave: handleSaveEnrollment,
            allowSave: !hasError && !readOnly && !areFiltersIncomplete,
            saveDisabledMessage: saveDisabledMessage
          })]
        })
      }), /*#__PURE__*/_jsxs(Switch, {
        children: [/*#__PURE__*/_jsx(Route, {
          exact: true,
          path: [Routes.FLOW_CANVAS_EVENT_ENROLLMENT, Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG_EVENT, Routes.PLATFORM_FLOW_REVIEW_ENROLLMENT],
          children: /*#__PURE__*/_jsxs(UIPanelBody, {
            style: {
              background: settingsComponent ? trellisTheme.color['do-not-use-gypsum'] : trellisTheme.color['do-not-use-olaf']
            },
            children: [/*#__PURE__*/_jsxs(UIPanelSection, {
              className: "m-bottom-0 p-bottom-8",
              style: {
                background: trellisTheme.color['do-not-use-olaf']
              },
              children: [/*#__PURE__*/_jsx(UIAlert, {
                use: "inline",
                className: "m-bottom-3",
                children: /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
                  baseLangKey: "automation-ui-enrollment.redesign.enrollSelection.manualEnrollAlert_cob",
                  objectTypeId: flow.objectTypeId
                })
              }), changeTriggersElement, /*#__PURE__*/_jsx(StepComponent, {
                shouldReenroll: shouldReenroll,
                setShouldReenroll: setShouldReenroll,
                bannerComponents: bannerComponent,
                setPanelStep: changePanelStep,
                objectTypeId: flow.objectTypeId,
                readOnly: readOnly ||
                /* if the user is still in edit mode and they chose to split their list triggers,
                SegmentationFilters won't show the updated value */
                hasError,
                setAreFiltersIncomplete: setAreFiltersIncomplete,
                setStagedChanges: setStagedChanges,
                stagedChanges: stagedChanges
              })]
            }), settingsComponent]
          })
        }), /*#__PURE__*/_jsx(Route, {
          exact: true,
          path: [Routes.FLOW_CANVAS_EVENT_UNENROLLMENT, Routes.FLOW_CANVAS_REVISION_UNENROLLMENT_CONFIG_EVENT],
          children: /*#__PURE__*/_jsx(UIPanelBody, {
            children: /*#__PURE__*/_jsx(UIPanelSection, {
              children: unenrollmentComponent
            })
          })
        }), /*#__PURE__*/_jsx(Route, {
          exact: true,
          path: [Routes.FLOW_CANVAS_EVENT_REENROLLMENT, Routes.FLOW_CANVAS_REVISION_REENROLLMENT_CONFIG_EVENT],
          children: /*#__PURE__*/_jsx(UIPanelBody, {
            children: /*#__PURE__*/_jsx(UIPanelSection, {
              children: /*#__PURE__*/_jsx(EventReenrollmentView, {
                shouldReenroll: shouldReenroll,
                objectTypeId: flow.objectTypeId,
                readOnly: readOnly,
                setShouldReenroll: checked => {
                  trackEvent(Trackables.TOGGLE_REENROLLMENT, {
                    enabled: checked,
                    enrollmentType: 'EVENT'
                  });
                  setShouldReenroll(checked);
                }
              })
            })
          })
        })]
      })]
    })
  });
};
EventEnrollmentContainerFromSegmentationFilters.passthruProps = {
  readOnly: PropTypes.bool
};
export default EventEnrollmentContainerFromSegmentationFilters;